import { z } from 'zod'
import { CENTER_ORDER, MAX_SCENARIO_NAME_LENGTH, MAX_WAIT_MINUTES, WAIT_STEP_MINUTES } from '@/services/allocationPriority/consts'
import type { FormState } from '../handoff/page.generated'
import { HOURS_ORD } from '../dispatchShared'

const dateValue = z.preprocess((value) => {
  if (typeof value === 'object' && value !== null && 'format' in value && typeof value.format === 'function') {
    return (value as { format: (pattern: string) => string }).format('YYYY-MM-DD')
  }
  return value
}, z.string())
const scenarioHourKey = z.string().regex(/^(?:[0-9]|1[0-9]|2[0-3])$/)
export const WAIT_MESSAGE = `대기시간은 0~${MAX_WAIT_MINUTES}분, ${WAIT_STEP_MINUTES}분 단위로 입력하세요.`
const waitMessage = WAIT_MESSAGE
/** 범위(정수 0~540)만 본다. 단위(30분)는 편집 전 값과 비교해 따로 본다 — isWaitStepAllowed. */
const waitRange = z
  .number({ invalid_type_error: waitMessage, required_error: waitMessage })
  .int(waitMessage)
  .min(0, waitMessage)
  .max(MAX_WAIT_MINUTES, waitMessage)
/**
 * 30분 단위는 사용자가 바꾼 값에만 요구한다. 서버(BE 계약: 정수 0~540)는 30분 단위가 아닌 값(예: 5분)을 가질 수 있어,
 * 편집 전 값(서버/스냅샷 값)을 그대로 다시 보내는 저장을 막지 않는다. original 이 없으면 항상 단위를 요구한다.
 */
export type OriginalWait = number | readonly number[] | undefined
export const isWaitStepAllowed = (wait: number, original?: OriginalWait): boolean =>
  (Array.isArray(original) ? original.includes(wait) : wait === original) || wait % WAIT_STEP_MINUTES === 0
const waitValueFor = (original?: OriginalWait) => waitRange.refine((wait) => isWaitStepAllowed(wait, original), waitMessage)
/** 대기시간 단건 검증. 실패 시 대기시간 안내 문구로 throw 한다. */
export const validateWait = (wait: unknown, original?: number): number => {
  const parsed = waitValueFor(original).safeParse(wait)
  if (!parsed.success) throw new Error(waitMessage)
  return parsed.data
}
/** 승인된 UI 어댑터: 19거점 전체 순열에서 센터의 상대 순서만 저장한다.
 * 입력을 먼저 검증하므로 누락/중복을 필터링으로 숨기지 않는다. 매장 이동은 정책에 반영하지 않는다. */
export const projectCenterOrder = (order: number[]): number[] => {
  if (
    !Array.isArray(order) ||
    ![CENTER_ORDER.length, 19].includes(order.length) ||
    new Set(order).size !== order.length ||
    Array.from(order).some((id) => !Number.isInteger(id) || id < 0 || id >= order.length)
  ) {
    throw new Error('센터 13개 또는 매장 포함 19개 전체 순위가 필요합니다. 중복·누락·잘못된 거점은 저장할 수 없습니다.')
  }
  return order.filter((id) => id < CENTER_ORDER.length)
}

export const TARGET_ORDER_MESSAGE = '창고 전체 순위가 필요합니다. 중복·누락·잘못된 창고는 저장할 수 없습니다.'
/** LOGISPRD-14424 실 모드: 서버 창고 targets(길이 targetCount) 인덱스의 순열만 허용한다. 투영하지 않고 복사본을 돌려준다. */
export const validateTargetOrder = (order: number[], targetCount: number): number[] => {
  if (
    !Array.isArray(order) ||
    order.length !== targetCount ||
    new Set(order).size !== order.length ||
    Array.from(order).some((id) => !Number.isInteger(id) || id < 0 || id >= targetCount)
  ) {
    throw new Error(TARGET_ORDER_MESSAGE)
  }
  return [...order]
}
/** 화면 순위 → 정책 순위. targetCount 가 없으면(테스트 픽스처) 13/19 투영, 있으면(실 모드) targets 순열 검증. */
export const toPolicyOrder = (order: number[], targetCount?: number): number[] =>
  targetCount === undefined ? projectCenterOrder(order) : validateTargetOrder(order, targetCount)
/** LOGISPRD-14424: 실 모드(targetCount 있음)는 서버 창고 targets 인덱스 순열과 순위 구간 길이를 검증한다. targetCount 없으면 13/19 투영. */
const orderValueFor = (targetCount?: number) =>
  z.array(z.number()).refine(
    (order) => {
      try {
        toPolicyOrder(order, targetCount)
        return true
      } catch {
        return false
      }
    },
    targetCount === undefined ? '센터 13개 또는 매장 포함 19개 전체 순위가 필요합니다.' : TARGET_ORDER_MESSAGE,
  )
/** 실 모드만 순위 구간 길이(0..창고 수)를 받는다. targetCount 없으면 ranked 가 없어야 한다. */
const rankedValueFor = (targetCount?: number) =>
  targetCount === undefined ? z.undefined() : z.number().int().min(0).max(targetCount).optional()
const scenarioHourValueFor = (targetCount?: number) =>
  z.object({ order: orderValueFor(targetCount), wait: waitRange, ranked: rankedValueFor(targetCount) })
export type ScenarioWaitField = 'hourDrafts' | 'savedHours'
/** 시간별 편집 전 대기시간(화면이 보여 줄 수 있는 값 여럿일 수 있다). 반환값과 같은 대기시간은 30분 단위를 요구하지 않는다. */
export type ScenarioOriginalWait = (field: ScenarioWaitField, hour: number) => OriginalWait
export const createScenarioFormSchema = (originalWait?: ScenarioOriginalWait, targetCount?: number) =>
  z
    .object({
      id: z.string().optional(),
      name: z.string().trim().min(1, '운영명을 입력하세요.').max(MAX_SCENARIO_NAME_LENGTH, `운영명은 ${MAX_SCENARIO_NAME_LENGTH}자 이내입니다.`),
      period: z.tuple([dateValue, dateValue]),
      loadFrom: z.string().nullable().optional(),
      hourDrafts: z.record(scenarioHourKey, scenarioHourValueFor(targetCount).nullable()).optional(),
      savedHours: z.record(scenarioHourKey, scenarioHourValueFor(targetCount)).optional(),
    })
    .superRefine((data, ctx) => {
      ;(['hourDrafts', 'savedHours'] as const).forEach((field) => {
        Object.entries(data[field] ?? {}).forEach(([hour, value]) => {
          if (value && !isWaitStepAllowed(value.wait, originalWait?.(field, Number(hour)))) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field, hour, 'wait'], message: waitMessage })
          }
        })
      })
    })
/** 편집 전 값 없이 검증하는 기본 스키마 — 모든 대기시간에 30분 단위를 요구한다. */
export const scenarioFormSchema = createScenarioFormSchema()
export const createHourFormSchema = (originalWait?: number, targetCount?: number) =>
  z.object({
    hr: z.number().int().min(0).max(23),
    dayOffset: z.number().int(),
    wait: waitValueFor(originalWait),
    order: orderValueFor(targetCount),
    ranked: rankedValueFor(targetCount),
    scope: z.enum(['one', 'after']).optional(),
  })
/** LOGISPRD-14596: 예외 적용 대상 시간 — one 은 선택 시간만, after 는 운영일 순서(23, 0..22)로 선택 시간부터 22시까지. */
export type ExceptionScope = 'one' | 'after'
export const exceptionScopeHours = (hr: number, scope?: ExceptionScope): number[] =>
  scope === 'after' ? HOURS_ORD.slice(HOURS_ORD.indexOf(hr)) : [hr]
/** 편집 전 값 없이 검증하는 기본 스키마 — 대기시간에 30분 단위를 요구한다. */
export const hourFormSchema = createHourFormSchema()
export const requiredForm = (form: FormState | undefined): FormState => {
  if (!form) throw new Error('생성본 UI에서 대상 폼이 연결되지 않아 처리할 수 없습니다.')
  return form
}
